import React, { Component } from 'react';
import { createPortal } from 'react-dom';
import _isEqual from 'lodash.isequal';
import _isEmpty from 'lodash.isempty';
import cx from 'classnames';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import _memoize from 'lodash.memoize';
import 'animate.css';
import Draggable from 'react-draggable';

import memoizeOne from 'memoize-one';
import { isAnonymousUser, getCurrentUserId } from '../../../../../common/utils';
import CommentButton from './CommentButton';
import CommentPopover from '../../CommentPopover';
import CommentsOverlayButton from './CommentsOverlayButton';
import Indicators from './Indicators';
import calculateCommentPercentPosition from '../utils/calculateCommentPercentPosition';
import { addCommentPosition, getCommentPosition } from '../utils/commentsTransformPosition';
import getCommentsToRender from '../utils/getCommentsToRender';

import '../styles/_comments-overlay.scss';

const ANIMATION_TIMEOUT = 10;
const CLOSE_MODAL_ORIGIN = 'comments-overlay';
// Below this width/height (in % of the overlay) a drawn box is treated as a
// point comment (a dot) rather than an area.
const MIN_REGION_PERCENT = 2;





















































export default class CommentsOverlay extends Component {






  handleClickComment = _memoize((comment) => () => {
    const {
      showCommentModal,
      onAddComment,
      onDeleteComment,
      selectCommentTab
    } = this.props;

    const { commentId } = comment;

    showCommentModal(
      commentId,
      {
        onOuterAction: this.onPopoverOuterAction,
        onAddComment,
        onDeleteComment
      }
    );
    if (typeof selectCommentTab === 'function') {
      selectCommentTab();
    }
  });

  constructor(props) {
    super(props);

    this.startPosition = React.createRef();
    this.startPosition.current = {
      commentId: null,
      x: 0,
      y: 0
    };

    this.state = {
      visibleComments: this.getSelectableComments(),
      newComment: undefined,
      canShowCreateCommentPopover: true,
      updatingComment: false,
      pinnedCommentId: null,
      pinnedAtTime: null
    };
  }

  // Pin the focused comment to the playhead position it was selected at.
  // Selecting a comment in the drawer (including opening it for editing) does
  // not seek the player, so its marker is drawn over the current frame — but
  // only until playback moves on, otherwise the orb would never disappear.
  static getDerivedStateFromProps({ selectedComment, time }, { pinnedCommentId }) {
    const { commentId = null } = selectedComment || {};

    if (commentId === pinnedCommentId) {return null;}

    return { pinnedCommentId: commentId, pinnedAtTime: time };
  }

  componentDidUpdate(prevProps) {
    const { time, comments, commentModalId, selectedComment } = this.props;

    // if selected comment id is reset to nothing
    if (!commentModalId && Boolean(prevProps.commentModalId)) {
      this.handleModalClose();
    }

    if (prevProps.time === time) {
      this._paused = true;
    }

    if (prevProps.time !== time || prevProps.comments !== comments) {
      this.handlePlaying();
    }

    if (commentModalId && prevProps.commentModalId !== commentModalId) {
      this.moveToComment(commentModalId);
    }

    // Watch for clicks outside the player only while a comment is selected.
    if (Boolean(selectedComment) !== Boolean(prevProps.selectedComment)) {
      this.toggleSelectionOuterListener(Boolean(selectedComment));
    }
  }

  componentWillUnmount() {
    this.toggleSelectionOuterListener(false);
  }

  toggleSelectionOuterListener = (enable) => {
    const method = enable ? 'addEventListener' : 'removeEventListener';
    document[method]('mousedown', this.onSelectionOuterAction);
  };

  // Clear the selection (hiding its area border) only when the click lands
  // outside the player. Clicks inside — including the control bar (rendered
  // outside .player during video review) or closing the comment box — keep it.
  onSelectionOuterAction = ({ target }) => {
    const { setSelectedComment } = this.props;
    if (!setSelectedComment || !target.closest) {return;}

    if (target.closest('.player, .player__floating')) {return;}

    setSelectedComment(null);
  };

  onPopoverOuterAction = (event) => {
    const { closePopover } = this.props;

    const selectedComment = document.querySelector('.comment-button--selected');

    // Unselect the selected comment if the click was not on the button
    if (selectedComment && !selectedComment.contains(event.target)) {
      closePopover({ closeModalOrigin: CLOSE_MODAL_ORIGIN });
    }
  };

  getCommentPercentPosition = (
  eventX,
  eventY,
  elementSize = 0) =>
  {
    const container = document.querySelector('.comments-overlay');

    if (!container) {
      return null;
    }

    return calculateCommentPercentPosition(container, eventX, eventY, elementSize);
  };

  getSelectableComments = () => {
    const { time, comments } = this.props;

    return getCommentsToRender({ comments, time });
  };

  getStageFromLocation = memoizeOne(({ hash }) => hash ? parseInt(hash.substr(1), 10) : 1);

  moveToComment = (commentId) => {
    const {
      pauseTime,
      comments,
      setCustomTime
    } = this.props;
    const { newComment } = this.state;

    if (newComment) {return;}

    this._paused = true;

    pauseTime();

    const selectedComment = comments.
    find((comment) => comment.commentId === commentId);

    if (!selectedComment) {return;}

    setCustomTime(selectedComment.timestamp);
  };

  updateIndicatorPosition = async (
  commentId,
  content,
  assetIds,
  metadata) =>
  {
    this.setState({ updatingComment: true });
    const { updateComment } = this.props;
    await updateComment(commentId, {
      content,
      assetIds,
      metadata
    });
    this.setState({ updatingComment: false });
  };

  handleModalClose = () => {
    this.disableOverlay();

    this.setState({ newComment: void 0 });
  };

  disableOverlay = () => {
    this._isOverlayDisabled = true;

    setTimeout(() => {
      this._isOverlayDisabled = false;
    }, 200);
  };

  dragStartHandler = (comment) => (e, dragData) => {
    const { commentId } = comment;
    const { closePopover } = this.props;

    const { lastX, lastY } = dragData || {};

    this.startPosition.current = {
      commentId,
      x: lastX,
      y: lastY
    };

    this.setState({ canShowCreateCommentPopover: true });

    if (commentId) {
      closePopover({ closeModalOrigin: CLOSE_MODAL_ORIGIN });
    } else {
      this.setState({ canShowCreateCommentPopover: false });
    }
  };

  dragStopHandler = (comment) => (e, dragData) => {
    const { content, assetIds, metadata, commentId, createdByUser: { userId } = {} } = comment;
    const { showCommentModal, onAddComment, onDeleteComment, isV2Design } = this.props;
    addCommentPosition(commentId, { x: dragData.lastX, y: dragData.lastY });
    const { updatingComment } = this.state;
    const hasPositionChanged = this.startPosition.current &&
    commentId === this.startPosition.current.commentId && (
    this.startPosition.current.x !== dragData.lastX ||
    this.startPosition.current.y !== dragData.lastY);


    if (metadata) {
      const position = this.getCommentPercentPosition(e.clientX, e.clientY);
      const xPercent = Number(position.x) || 50;
      const yPercent = Number(position.y) || 50;

      const metaDataObject = JSON.parse(metadata);

      const newMetaData = {
        ...metaDataObject,
        xPercent,
        yPercent
      };

      if ((!isAnonymousUser() || isAnonymousUser() && getCurrentUserId() === userId) &&
      !updatingComment && hasPositionChanged) {
        this.updateIndicatorPosition(
          commentId,
          content,
          assetIds,
          JSON.stringify(newMetaData)
        );
      }

      // For V2 design, don't show popover for existing comments (use drawer instead)
      if (!isV2Design) {
        showCommentModal(commentId, {
          onOuterAction: this.onPopoverOuterAction,
          onAddComment,
          onDeleteComment
        });
      }
    } else {
      this.setState({ canShowCreateCommentPopover: true });
    }
  };

  handlePlaying = () => {
    const { visibleComments: currentVisibleComments } = this.state;

    this._paused = false;

    const newVisibleComments = this.getSelectableComments();

    if (_isEqual(currentVisibleComments, newVisibleComments)) {return;}

    return void this.setState({ visibleComments: newVisibleComments });
  };

  getDefaultVisibility = () => {
    const { allowedWriteVisibilityOptions, isInternalMode } = this.props;
    if (isInternalMode) return 'SHOOTSTA_INTERNAL';

    if (
    allowedWriteVisibilityOptions &&
    allowedWriteVisibilityOptions.length)
    return allowedWriteVisibilityOptions[0];

    return 'PUBLIC';
  };

  onOverlayClick = (event) => {
    if (this._isOverlayDisabled) {return;}
    const ELEMENT_SIZE = 55;
    const {
      pauseTime,
      time,
      getUserColor,
      comments,
      showCommentModal,
      onAddComment,
      selectedClip = {},
      assetId,
      commentModalId,
      isVideoCompleted,
      isV2Design,
      commentOrigin,
      setSelectedComment
    } = this.props;

    if (!isVideoCompleted && commentModalId && !isV2Design) {
      return;
    }

    // Clear previous selection so the new dot renders bright.
    if (setSelectedComment) setSelectedComment(null);

    this.setState({ canShowCreateCommentPopover: true });
    pauseTime();

    const percents = this.getCommentPercentPosition(event.clientX, event.clientY, ELEMENT_SIZE);
    const newCommentId = Date.now().toString();
    const { clipId, clipStartTime = 0 } = selectedClip;
    const clipCommentTime = time - clipStartTime;
    const stage = this.getStageFromLocation(window.location);
    const metadata = {
      xPercent: parseInt(percents.x, 10),
      yPercent: parseInt(percents.y, 10),
      timestamp: time,
      stage,
      ...(clipId ?
      {
        clipId,
        clipCommentTime
      } :
      { assetId }),

      ...(isV2Design && _isEmpty(selectedClip) ?
      {
        visibility: this.getDefaultVisibility(),
        commentOrigin
      } :
      {})
    };

    // A new comment is always the newest, so it gets the next number.
    const newCommentNumber = comments.length + 1;

    const newComment = {
      id: newCommentId,
      colour: getUserColor(),
      number: newCommentNumber,
      ...metadata
    };

    this.setState({ newComment }, () => {
      showCommentModal(
        newCommentId,
        {
          onAddComment,
          metadata,
          onOuterAction: this.onPopoverOuterAction
        }
      );
    });
  };

  onDrawBox = ({ startX, startY, endX, endY }) => {
    if (this._isOverlayDisabled) {return;}
    const {
      pauseTime,
      time,
      getUserColor,
      comments,
      showCommentModal,
      onAddComment,
      selectedClip = {},
      assetId,
      commentModalId,
      isVideoCompleted,
      isV2Design,
      commentOrigin,
      setSelectedComment
    } = this.props;

    if (!isVideoCompleted && commentModalId && !isV2Design) {
      return;
    }

    // Use the raw corner positions (elementSize 0) rather than the dot-centred
    // offset used for point comments.
    const start = this.getCommentPercentPosition(startX, startY);
    const end = this.getCommentPercentPosition(endX, endY);

    if (!start || !end) {return;}

    const x1 = Number(start.x);
    const y1 = Number(start.y);
    const x2 = Number(end.x);
    const y2 = Number(end.y);

    const xPercent = Math.max(0, Math.min(x1, x2));
    const yPercent = Math.max(0, Math.min(y1, y2));
    const regionWidthPercent = Math.min(100 - xPercent, Math.abs(x2 - x1));
    const regionHeightPercent = Math.min(100 - yPercent, Math.abs(y2 - y1));

    // Too small to be a meaningful area – fall back to a point comment.
    if (regionWidthPercent < MIN_REGION_PERCENT || regionHeightPercent < MIN_REGION_PERCENT) {
      this.onOverlayClick({ clientX: startX, clientY: startY });
      return;
    }

    if (setSelectedComment) {setSelectedComment(null);}

    this.setState({ canShowCreateCommentPopover: true });
    pauseTime();

    const newCommentId = Date.now().toString();
    const { clipId, clipStartTime = 0 } = selectedClip;
    const clipCommentTime = time - clipStartTime;
    const stage = this.getStageFromLocation(window.location);
    const metadata = {
      xPercent,
      yPercent,
      regionWidthPercent,
      regionHeightPercent,
      timestamp: time,
      stage,
      ...(clipId ?
      {
        clipId,
        clipCommentTime
      } :
      { assetId }),

      ...(isV2Design && _isEmpty(selectedClip) ?
      {
        visibility: this.getDefaultVisibility(),
        commentOrigin
      } :
      {})
    };

    // A new comment is always the newest, so it gets the next number.
    const newCommentNumber = comments.length + 1;

    const newComment = {
      id: newCommentId,
      colour: getUserColor(),
      number: newCommentNumber,
      ...metadata
    };

    this.setState({ newComment }, () => {
      showCommentModal(
        newCommentId,
        {
          onAddComment,
          metadata,
          onOuterAction: this.onPopoverOuterAction
        }
      );
    });
  };

  renderComment = ({
    comment, commentModalId
  }) => {
    const {
      onInputFocus,
      sceneId,
      shotId,
      isV2Design,
      onVisibilityChange,
      selectedClip,
      selectedComment,
      setSelectedComment,
      hideTimestamp,
      isVideoCompleted: isVisibilityHidden
    } = this.props;
    const { canShowCreateCommentPopover } = this.state;
    const { commentId: selectedCommentId = '' } = selectedComment || {};

    const {
      id,
      assetId,
      commentId,
      xPercent: xValue,
      yPercent: yValue,
      regionWidthPercent,
      regionHeightPercent,
      colour,
      isAddedByCommentForm
    } = comment;

    const isCommentAddedByForm = typeof isAddedByCommentForm === 'undefined' && !assetId || isAddedByCommentForm;

    const xPercent = isCommentAddedByForm ? 50 : xValue; // If comment added by form display popover in middle
    const yPercent = isCommentAddedByForm ? 50 : yValue;

    // An area/box comment carries a width & height (% of the overlay) alongside
    // the top-left corner (xPercent/yPercent). Absent ⇒ a plain point (dot).
    const isRegion = !isCommentAddedByForm &&
    Number(regionWidthPercent) > 0 && Number(regionHeightPercent) > 0;

    const translatePosition = commentId && getCommentPosition(commentId) ?
    getCommentPosition(commentId) : { x: 0, y: 0 };

    const isSelected = isV2Design ?
    selectedCommentId === commentId :
    [commentModalId, selectedCommentId].includes(commentId);

    // Only the selected comment's area is drawn (plus the one being created,
    // which has no commentId yet) so overlapping boxes don't clutter the frame.
    const showRegionBox = isRegion && (isSelected || !commentId);

    const commentButton = (
      <CommentButton
        onClick={() => setSelectedComment && setSelectedComment(comment)}
        selected={isSelected}
        isV2Design={isV2Design}
        selectedCommentId={selectedCommentId}
        {...comment} />);

    return (
      <CSSTransition
        key={commentId}
        classNames={{
          enter: 'animated',
          enterActive: 'bounceIn',
          exit: 'animated',
          exitActive: 'bounceOut'
        }}
        timeout={ANIMATION_TIMEOUT}>

        <>
          {showRegionBox &&
          <div
            className="comment-region-box"
            style={{
              position: 'absolute',
              left: `${xPercent}%`,
              top: `${yPercent}%`,
              width: `${regionWidthPercent}%`,
              height: `${regionHeightPercent}%`,
              borderColor: colour && colour.color
            }} />}


          {/* Area comments show a static marker pinned to the box corner;
              point comments keep the draggable dot. */}
          {isRegion && !isCommentAddedByForm &&
          <div
            className="draggable-container"
            style={{
              position: 'absolute',
              zIndex: 5,
              left: `${xPercent}%`,
              top: `${yPercent}%`
            }}>

              {commentButton}
            </div>}


          {!isRegion && !isCommentAddedByForm &&
          <Draggable
            bounds="parent"
            onStart={this.dragStartHandler(comment)}
            onStop={this.dragStopHandler(comment)}
            defaultPosition={{ x: translatePosition.x, y: translatePosition.y }}>

              <div
              className="draggable-container"
              style={{
                position: 'absolute',
                zIndex: 5,
                left: `${xPercent}%`,
                top: `${yPercent}%`
              }}>

                {commentButton}
              </div>
            </Draggable>}


          {
          canShowCreateCommentPopover &&
          <CommentPopover
            id={commentId || id}
            commentId={commentId}
            sceneId={sceneId}
            shotId={shotId}
            onInputFocus={onInputFocus}
            onVisibilityChange={onVisibilityChange}
            isV2Design={isV2Design}
            hideVisibility={!_isEmpty(selectedClip) || isVisibilityHidden}
            hideTimestamp={hideTimestamp}>

                <div
              style={{
                height: '50px',
                width: '50px',
                position: 'absolute',
                left: `${xPercent}%`,
                top: `${yPercent}%`,
                transform: `translate(${translatePosition.x}px, ${translatePosition.y}px)`
              }} />

              </CommentPopover>}


        </>
      </CSSTransition>);

  };

  render() {
    const {
      className,
      selector,
      totalDuration,
      commentsOverlayClass,
      isReadOnly,
      comments,
      commentModalId,
      time,
      hideIndicators,
      isV2Design,
      isVideoCompleted
    } = this.props;

    const { newComment, pinnedCommentId, pinnedAtTime } = this.state;

    const overlayParent = document.querySelector(selector);

    const shouldShowOverlayTooltip = isVideoCompleted ?
    !newComment : !commentModalId || isV2Design;

    // Markers show only while the playhead is within COMMENT_THRESHOLD_MS of their
    // timestamp. The selected comment stays rendered so its area box remains —
    // but only while the playhead is still parked where it was selected, so
    // resuming playback hides it like any other marker.
    const commentsToRender = getCommentsToRender({
      comments,
      time,
      pinnedCommentId: time === pinnedAtTime ? pinnedCommentId : null
    });

    return (
      <div className={cx('comments-overlay', className)}>
        {
        !hideIndicators && overlayParent && createPortal(
          <Indicators
            comments={comments}
            totalDuration={totalDuration}
            className={cx('comments-overlay__comments', commentsOverlayClass)}
            onClickIndicator={this.handleClickComment}
            isV2Design={isV2Design} />,

          overlayParent
        )}

        <TransitionGroup component={null}>
          {commentsToRender.map((comment) => this.renderComment({ comment, commentModalId }))}
        </TransitionGroup>
        {newComment ? this.renderComment({ comment: newComment }) : null}
        {!isReadOnly &&
        <CommentsOverlayButton
          onClick={this.onOverlayClick}
          onDrawBox={this.onDrawBox}
          shouldShowTooltip={shouldShowOverlayTooltip} />}


      </div>);

  }
}